import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// --- HELPER: SIEVE LOGIC ---
const getSieveSize = (diameter) => {
    if (diameter >= 1.25 && diameter <= 1.80) return "+2";
    if (diameter >= 1.81 && diameter <= 2.10) return "+6.5";
    if (diameter >= 2.11 && diameter <= 2.30) return "+8";
    if (diameter >= 2.31 && diameter <= 2.50) return "+9";
    if (diameter >= 2.51 && diameter <= 2.70) return "+10";
    if (diameter >= 2.71 && diameter <= 3.30) return "+11";
    if (diameter >= 3.31 && diameter <= 3.50) return "+14";
    if (diameter >= 3.51 && diameter <= 3.70) return "+15";
    if (diameter >= 3.71 && diameter <= 3.90) return "+18";
    if (diameter >= 3.91 && diameter <= 5.00) return "+23";
    return "-2"; // Fallback
};

// --- HELPER: SIEVE SORT ORDER ---
const sieveOrder = ["-2", "+2", "+6.5", "+8", "+9", "+10", "+11", "+14", "+15", "+18", "+23"];

export const generatePlanningReport = (parentKapan, packets) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const margin = 14;
    
    // --- 1. DATA PREPARATION ---
    let allStones = [];
    packets.forEach(p => {
        if (p.planningData && p.planningData.stones) {
            allStones = [...allStones, ...p.planningData.stones];
        }
    });

    if (allStones.length === 0) {
        alert("No stone data found in these packets.");
        return;
    }

    // -- Header Calculations --
    const roughPcs = parentKapan.pieces || 1; 
    const roughCts = parentKapan.weight || 0;
    const rSize = roughPcs > 0 ? (roughCts / roughPcs).toFixed(3) : "0";
    const chadtar = roughCts > 0 ? (roughPcs / roughCts).toFixed(2) : "0";

    // -- HELPER: Calculate Summary Metrics --
    const calcMetrics = (stones) => {
        const pcs = stones.length;
        const cts = stones.reduce((sum, s) => sum + (s.weight || 0), 0);
        const val = stones.reduce((sum, s) => sum + (s.value || 0), 0);
        
        const size = roughCts > 0 ? (pcs / roughCts).toFixed(2) : "0";
        const recovery = roughCts > 0 ? ((cts / roughCts) * 100).toFixed(2) : "0";

        return [
            pcs,
            cts.toFixed(3),
            size,
            recovery + "%",
            "$" + val.toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0})
        ];
    };

    // -- Filter Data --
    const stones01 = allStones.filter(s => s.weight >= 0.01);
    const stones008 = allStones.filter(s => s.weight >= 0.008); 
    const stonesIF = stones008.filter(s => s.clarity === "IF"); 

    // --- 2. AGGREGATE DATA ---
    const sieveMap = {}, diaMap = {}, clarityMap = {}, colorMap = {}, ifDiaMap = {};
    const filteredTotalCts = stones008.reduce((sum, s) => sum + (s.weight || 0), 0);
    const ifTotalCts = stonesIF.reduce((sum, s) => sum + (s.weight || 0), 0); 
    
    // Custom Diameter Ranges
    const diaRanges = [
        { min: 0, max: 1.25 }, { min: 1.26, max: 1.35 }, { min: 1.36, max: 1.45 },
        { min: 1.46, max: 1.49 }, { min: 1.50, max: 1.55 }, { min: 1.56, max: 1.74 },
        { min: 1.75, max: 1.80 }, { min: 1.81, max: 2.00 }, { min: 2.01, max: 2.30 },
        { min: 2.31, max: 2.70 }, { min: 2.71, max: 2.99 }, { min: 3.00, max: 10.0 }
    ];

    // -- Process Data (ALL TABLES use stones008) --
    stones008.forEach(s => {
        // A. Sieve
        const sieve = getSieveSize(s.diameter);
        if (!sieveMap[sieve]) sieveMap[sieve] = { pcs: 0, cts: 0 };
        sieveMap[sieve].pcs++; sieveMap[sieve].cts += s.weight;

        // B. Diameter
        const d = s.diameter;
        const range = diaRanges.find(r => d >= r.min && d <= r.max);
        const label = range ? `${range.min.toFixed(2)}-${range.max.toFixed(2)}` : "Other";
        if (!diaMap[label]) diaMap[label] = { cts: 0 };
        diaMap[label].cts += s.weight;

        // C. Clarity
        const cla = s.clarity || "Unk";
        if (!clarityMap[cla]) clarityMap[cla] = { pcs: 0, cts: 0 };
        clarityMap[cla].pcs++; clarityMap[cla].cts += s.weight;

        // D. Color
        const col = s.color || "Unk";
        if (!colorMap[col]) colorMap[col] = { cts: 0 };
        colorMap[col].cts += s.weight;
    });

    // -- Process Data for IF Table --
    stonesIF.forEach(s => {
        const d = s.diameter;
        const range = diaRanges.find(r => d >= r.min && d <= r.max);
        const label = range ? `${range.min.toFixed(2)}-${range.max.toFixed(2)}` : "Other";
        if (!ifDiaMap[label]) ifDiaMap[label] = { cts: 0 };
        ifDiaMap[label].cts += s.weight;
    });

    // --- 3. HELPER TO CREATE TABLE ROWS ---
    // Added 'globalTotalCts' parameter for the 2nd percentage column
    const createRows = (mapData, showPcs = true, isSieve = false, localTotalCts = null, globalTotalCts = null) => {
        let keys = Object.keys(mapData);
        
        if (isSieve) {
            keys.sort((a, b) => {
                const idxA = sieveOrder.indexOf(a);
                const idxB = sieveOrder.indexOf(b);
                return (idxA === -1 ? 99 : idxA) - (idxB === -1 ? 99 : idxB);
            });
        } else {
            keys.sort();
        }

        // Use filteredTotalCts if no specific local total is provided
        const currentTotalCts = localTotalCts !== null ? localTotalCts : filteredTotalCts;
        
        // Sum totals for the table footer
        let tableTotalCts = 0;
        let tableTotalPcs = 0;

        keys.forEach(k => {
            tableTotalCts += mapData[k].cts;
            if(showPcs) tableTotalPcs += (mapData[k].pcs || 0);
        });

        const rows = keys.map(k => {
            const item = mapData[k];
            const per = currentTotalCts > 0 ? ((item.cts / currentTotalCts) * 100).toFixed(1) : "0.0";
            
            // Base row data
            let rowData = showPcs 
                ? [k, item.pcs, item.cts.toFixed(3), per] 
                : [k, item.cts.toFixed(3), per];

            // If a Global Total is provided (for IF table), add the 2nd percentage column
            if (globalTotalCts !== null) {
                const perGlobal = globalTotalCts > 0 ? ((item.cts / globalTotalCts) * 100).toFixed(1) : "0.0";
                rowData.push(perGlobal);
            }

            return rowData;
        });

        // Add Total Row
        let totalRowData = showPcs 
            ? ["TOTAL", tableTotalPcs, tableTotalCts.toFixed(3), "100.0"] 
            : ["TOTAL", tableTotalCts.toFixed(3), "100.0"];

        // Add Total for the 2nd percentage column if active
        if (globalTotalCts !== null) {
            const totalPerGlobal = globalTotalCts > 0 ? ((tableTotalCts / globalTotalCts) * 100).toFixed(1) : "0.0";
            totalRowData.push(totalPerGlobal);
        }

        rows.push(totalRowData);
        return rows;
    };

    // --- 4. PDF GENERATION ---

    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.setTextColor(41, 128, 185); 
    doc.text("ESTIMATE PLAN REPORT", margin, 20);
    
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Date: ${new Date().toLocaleString()}`, pageWidth - margin - 50, 20);
    doc.line(margin, 25, pageWidth - margin, 25); 

    const origin = parentKapan.cutId?.origin || parentKapan.roughOrigin || "Unknown";

    // 1. Rough Stone Details
    autoTable(doc, {
        startY: 30,
        head: [['Kapan', 'Rough Type', 'RoughPcs', 'Rough Cts', 'R Size', 'Chadtar']],
        body: [[
            parentKapan.kapanName, 
            origin,
            roughPcs,
            roughCts.toFixed(2),
            rSize,
            chadtar
        ]],
        theme: 'plain',
        styles: { fontSize: 9, cellPadding: 2, halign: 'center' },
        headStyles: { fontStyle: 'bold', textColor: 50 },
        columnStyles: { 0: { fontStyle: 'bold' } } 
    });

    // 2. Plan Summary
    autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 5,
        head: [['Criteria', 'Plan Pcs', 'Exp. Cts', 'Size', 'Yield %', 'Total Value']],
        body: [
            ["+0.01", ...calcMetrics(stones01)],
            ["+0.008", ...calcMetrics(stones008)],
            ["ALL", ...calcMetrics(allStones)]
        ],
        theme: 'grid',
        headStyles: { fillColor: [44, 62, 80], textColor: 255, halign: 'center' },
        styles: { fontSize: 10, halign: 'center' },
        columnStyles: { 0: { cellWidth: 30, halign: 'left', fontStyle: 'bold' } }
    });

    let currentY = doc.lastAutoTable.finalY + 10;
    const halfWidth = (pageWidth - (margin * 2)) / 2 - 5; 
    
    // Left: Sieves
    doc.setFontSize(11);
    doc.setTextColor(0);
    doc.text("Sieves", margin, currentY - 2);
    
    autoTable(doc, {
        startY: currentY,
        margin: { left: margin },
        tableWidth: halfWidth,
        head: [['Sieve', 'Pcs', 'Cts', '%']],
        body: createRows(sieveMap, true, true, filteredTotalCts), 
        theme: 'striped',
        styles: { fontSize: 8, halign: 'right' },
        columnStyles: { 0: { halign: 'left', fontStyle: 'bold' } },
        footStyles: { fillColor: [200, 200, 200], textColor: 0, fontStyle: 'bold' }
    });
    
    const leftHeight = doc.lastAutoTable.finalY;

    // Right: Diameters
    doc.text("Diameters", margin + halfWidth + 10, currentY - 2);
    
    autoTable(doc, {
        startY: currentY,
        margin: { left: margin + halfWidth + 10 },
        tableWidth: halfWidth,
        head: [['Diameter', 'Cts', '%']],
        body: createRows(diaMap, false, false, filteredTotalCts),
        theme: 'striped',
        styles: { fontSize: 8, halign: 'right' },
        columnStyles: { 0: { halign: 'left', fontStyle: 'bold' } }
    });

    const rightHeight = doc.lastAutoTable.finalY;
    
    currentY = Math.max(leftHeight, rightHeight) + 15;
    if (currentY > 250) { doc.addPage(); currentY = 20; }

    // Left: Clarity
    doc.text("Clarity", margin, currentY - 2);
    
    autoTable(doc, {
        startY: currentY,
        margin: { left: margin },
        tableWidth: halfWidth,
        head: [['Clarity', 'Pcs', 'Cts', '%']],
        body: createRows(clarityMap, true, false, filteredTotalCts),
        theme: 'striped',
        styles: { fontSize: 8, halign: 'right' },
        columnStyles: { 0: { halign: 'left', fontStyle: 'bold' } }
    });

    const leftHeight2 = doc.lastAutoTable.finalY;

    // Right: Color
    doc.text("Color", margin + halfWidth + 10, currentY - 2);
    
    autoTable(doc, {
        startY: currentY,
        margin: { left: margin + halfWidth + 10 },
        tableWidth: halfWidth,
        head: [['Color', 'Cts', '%']],
        body: createRows(colorMap, false, false, filteredTotalCts),
        theme: 'striped',
        styles: { fontSize: 8, halign: 'right' },
        columnStyles: { 0: { halign: 'left', fontStyle: 'bold' } }
    });

    const rightHeight2 = doc.lastAutoTable.finalY;

    // --- NEW SECTION: IF Diameters ---
    currentY = Math.max(leftHeight2, rightHeight2) + 15;
    if (currentY > 240) { doc.addPage(); currentY = 20; }

    doc.text("IF Diameters", margin, currentY - 2);
    
    autoTable(doc, {
        startY: currentY,
        margin: { left: margin },
        tableWidth: halfWidth + 15, // Made slightly wider for extra column
        head: [['Diameter', 'Cts', '% (IF)', '% (Total)']],
        // Pass ifTotalCts for 1st %, filteredTotalCts for 2nd %
        body: createRows(ifDiaMap, false, false, ifTotalCts, filteredTotalCts),
        theme: 'striped',
        styles: { fontSize: 8, halign: 'right' },
        columnStyles: { 0: { halign: 'left', fontStyle: 'bold' } }
    });

    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.text(`Page ${i} of ${pageCount}`, pageWidth - 20, 285, { align: 'right' });
    }

    doc.save(`Estimate_Plan_${parentKapan.kapanName}.pdf`);
};